import { Box, Theme, Typography } from '@mui/material'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { SettingsListItem } from '@/shared/ui/SettingsListItem'
import { useTranslation } from 'react-i18next'
import {
    PersonIcon,
    Settings as SettingsIcon,
    UserIcon,
    TAndC,
} from '@/shared/ui/Icons'
import { LogoutButton } from '@/shared/ui/LogoutButton'
import { ProfileScreenModal } from '@/widgets/ProfileScreen/UI/ProfileScreenModal'
import { useAppStore, useVisibilityStore } from '@/shared/store'
import { ErrorBoundary } from 'react-error-boundary'
import { ErrorFallback } from '@/shared/ui'
import { defaultProfile, settingsPagePath } from '@/shared/constants'
import { Profile } from '@/shared/store/useAppStore'

export const SettingsPopper = ({ onClose }: { onClose: () => void }) => {
    const ref = useRef<HTMLDivElement | null>(null)
    const { t: translate } = useTranslation()
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [profile, setProfile] = useState<Profile | null>(null)
    const { actualProfile, profiles, tAndC, userEmail, isGuest } = useAppStore()
    const { setSettingsContentVisible } = useVisibilityStore()

    const settingsData: {
        icon: ReactNode
        name: string
        link: string
    }[] = [
        {
            icon: <SettingsIcon />,
            name: translate('settings'),
            link: settingsPagePath,
        },
        {
            // Polityka prywatności
            icon: <TAndC />,
            name: translate('privacyPolicy'),
            link: tAndC,
        },
    ]
    const handleSettingClick = () => {
        onClose()
        setSettingsContentVisible()
    }

    const renderSettings = () =>
        settingsData.map((item) => (
            <SettingsListItem
                key={item.name}
                icon={item.icon}
                name={item.name}
                onClose={handleSettingClick}
                link={item.link}
            />
        ))

    const userNameStyle = {
        display: 'block',
        typography: 'text2',
        lineHeight: '17px',
        height: '17px',
        color: 'custom.greyFontColor',
        paddingLeft: '12px',
    }

    const profileStyle = {
        display: 'flex',
        alignItems: 'center',
        padding: '0 4px 16px',
        borderBottom: (theme: Theme) =>
            `1px solid ${theme.palette.custom.neutralDark}`,
        cursor: isGuest ? 'default' : 'pointer',
    }

    const showModal = (value: boolean) => {
        setIsModalOpen(value)
    }

    const closeAll = () => {
        setIsModalOpen(false)
        onClose()
    }

    useEffect(() => {
        const updatedProfile = profiles.find(
            (profile) => profile.id === actualProfile?.id
        )
        setProfile(updatedProfile || defaultProfile)
    }, [profiles.length, profile?.id, actualProfile?.id, userEmail])

    return (
        <ErrorBoundary FallbackComponent={ErrorFallback}>
            <Box ref={ref}>
                <Box
                    sx={profileStyle}
                    onClick={() => !isGuest && showModal(true)}>
                    <Box display="flex" alignItems="center">
                        {profile?.isKid ? <UserIcon /> : <PersonIcon />}
                    </Box>
                    <Box>
                        <Typography variant="subtitle1" sx={userNameStyle}>
                            {profile?.profile_name}
                        </Typography>
                        {userEmail && (
                            <Typography sx={userNameStyle}>
                                {userEmail}
                            </Typography>
                        )}
                    </Box>
                </Box>
                {!isGuest && renderSettings()}
                <LogoutButton onClose={onClose} />
                <ProfileScreenModal
                    shouldShowModal={isModalOpen}
                    setShouldShowModal={closeAll}
                />
            </Box>
        </ErrorBoundary>
    )
}
